import {
  BaseProducts,
  Images,
  MconThemePrices,
  MconThemePriceTypes,
  MconThemes,
  MconThemesId,
  ProductsMconThemes,
  ProductsMconThemesImages,
  ProductsMconThemesImagesInitializer,
  ProductsMconThemesInitializer,
} from '@services/database/mco/schema/public';
import { parseSvgText } from '@modules/ProductColor/S/fs';
import {
  compact,
  each,
  entries,
  every,
  filter,
  flat,
  flatMap,
  groupBy,
  map,
  pipe,
  reduceLazy,
  some,
  toArray,
  uniq,
} from '@fxts/core';
import axios from 'axios';
import { ProductConstantS } from '@modules/Product/S/Constant/module/ProductConstantS.js';
import { OMPCreatorDesignS } from '@modules/OMP/CreatorDesign/S/Function/module/OMPCreatorDesignS';
import { UtilS } from '@modules/Util/S/Function/module/UtilS';
import { MconS } from '@modules/Mcon/S/Function/module/MconS';
import { MpMakerTypeTotalCvObj } from '@modules/NewMaker/Type/S/Constant';
import { MconStore } from '@modules/Mcon/S/mcon-type.ts';
import { MCON_LISENCE_TYPE } from '@modules/Mcon/S/Constant/mcon';

type CvImageObj = {
  _data: {
    cv_type: 'cv_image';
    mcon_theme_id: MconThemesId;
    image_id: number;
  };
};

type CvPatternObj = {
  _data: {
    cv_type: 'cv_pattern';
    cv_image_attrs: { _data: { mcon_theme_id: MconThemesId; image_id: number } };
  };
};

type CvObj = MpMakerTypeTotalCvObj;

interface ProductFaces2 {
  value: Array<{ designs: CvObj[] }>;
}

interface ThemeImageInput {
  product_faces2?: ProductFaces2;
  svg_el?: SVGElement;
  svg_els?: SVGElement[];
  svg_url?: string;
}

export type MconThemePriceTypeWithPrices = MconThemePriceTypes & {
  mcon_theme_prices: MconThemePrices[];
};

export interface MconTheme extends MconThemes {
  mcon_theme_price_type: MconThemePriceTypes;
  store: MconStore;
  images: number[]; // 에디터에서 사용되는 이미지들
}

export interface ProductsMconThemesAssociate extends ProductsMconThemes {
  _: {
    mcon_theme: MconThemes & {
      _: {
        store: MconStore;
        mcon_theme_price_type: MconThemePriceTypes;
      };
    };
    products_mcon_themes_images: (ProductsMconThemesImages & {
      _: {
        image: Images;
      };
    })[];
  };
}

export interface ProductsMconThemesForInsert extends ProductsMconThemesInitializer {
  _: {
    products_mcon_themes_images: ProductsMconThemesImagesInitializer[];
  };
}

export type ProductsMconThemesAssociateForMaker = {
  mcon_theme_id: number;
} & {
  _: {
    mcon_theme: MconThemes & {
      _: {
        store: MconStore;
        mcon_theme_price_type: MconThemePriceTypes;
      };
    };
    products_mcon_themes_images: (ProductsMconThemesImages & {
      _: {
        image: Images;
      };
    })[];
  };
};

export type ProductsMconThemeForMaker = {
  mcon_theme_id: number;
  quantity: number;
  price: number;
  percent: string;
} & {
  _: {
    mcon_theme: MconThemes & {
      _: {
        store: MconStore;
        mcon_theme_price_type: MconThemePriceTypes;
      };
    };
    products_mcon_themes_images: {
      image_id: number;
    }[];
  };
};

export const CUSTOME_PERMISSION = {
  NORMAL: 'NORMAL',
  RESTRICTED: 'RESTRICTED',
};

export class ProductMconTheme {
  // 한번이라도 에디터에 추가된 theme은 map에 들어간다.
  // 왜냐하면 잘라내기, undo/redo 등 작업시 theme을 찾기 위해서!
  // 실제 현재 사용 중인 theme은 images에 데이터가 들어가 있는 것.
  private mcon_theme_map: Map<number, MconTheme>;

  constructor(mcon_theme_map: Map<number, MconTheme> | null | undefined) {
    this.mcon_theme_map = mcon_theme_map ?? new Map();
  }

  validateMarppleDesignInput(): { success: boolean; message: string } {
    //! 컬럼이 만들어져야 함
    throw new Error('Method not implemented.');
  }

  validateThemeImage(mcon_theme_id: MconThemesId, has_marpple_design: boolean) {
    /* 2차 장작 제한인 테마가 이미 있는 경우 */
    if (has_marpple_design) {
      if (!this.isMarppleDesignPossible(mcon_theme_id)) {
        /* 마플 디자인이 있는데, 넣으려고 하는 테마가 2차 장작 허용 안하는 경우 */
        return {
          success: false,
          code: 'E100-1',
          /* 2차 장작 제한인 테마 넣으려는 경우 */
        };
      }
    }
    if (this.hasTheme()) {
      if (this.hasOnlySameStore(mcon_theme_id)) {
        return {
          success: true,
          code: 'G100',
        };
      } else {
        const current_themes = this.getCurrentMconThemes();
        if (current_themes.some((mcon_theme) => !this.isMultiThemePossible({ mcon_theme }))) {
          /* 이미 넣은 테마가 2차 장작 허용 안하는 경우 */
          return {
            success: false,
            code: 'E200',
            /* 2차 장작 제한인 테마가 이미 있는 경우 */
          };
        }
        if (!this.isMultiThemePossible({ mcon_theme_id })) {
          /* 이미 테마가 있는데, 넣으려고 하는 테마가 2차 장작 허용 안하는 경우 */
          return {
            success: false,
            code: 'E100-2',
            /* 2차 장작 제한인 테마 넣으려는 경우 */
          };
        }
      }
    }
    return {
      success: true,
      code: 'G200',
    };
  }

  public static checkProductsMconTheme = (products_mcon_theme: ProductsMconThemesAssociate, _en: string) => {
    return MconS.checkCanSellingMconTheme(
      {
        mcon_theme: products_mcon_theme._.mcon_theme,
        products_mcon_themes_images: products_mcon_theme._.products_mcon_themes_images,
      },
      _en,
    );
  };

  static isCvImageObj(obj: CvObj): obj is CvImageObj {
    return obj._data.cv_type === 'cv_image';
  }

  static isCvPatternObj(obj: CvObj): obj is CvPatternObj {
    return obj._data.cv_type === 'cv_pattern';
  }

  private isSameTheme(mcon_theme_id: MconThemesId) {
    const mcon_themes = this.getCurrentMconThemes();
    return some((mcon_theme) => mcon_theme.id === mcon_theme_id, mcon_themes);
  }

  private hasOnlySameStore(mcon_theme_id: MconThemesId) {
    const target_mcon_theme = this.getMconTheme(mcon_theme_id);
    if (!target_mcon_theme) return false;
    const mcon_themes = this.getCurrentMconThemes();
    return every((mcon_theme) => mcon_theme.store.id === target_mcon_theme.store_id, mcon_themes);
  }

  public isSameThemeCvObj(cv_obj: CvObj): boolean {
    const cv_image_obj = ProductMconTheme.isCvImageObj(cv_obj);
    const cv_pattern_obj = ProductMconTheme.isCvPatternObj(cv_obj);

    if (cv_image_obj) {
      return this.isSameTheme(cv_obj._data.mcon_theme_id);
    }

    if (cv_pattern_obj) {
      const pattern_obj: CvPatternObj = cv_obj;
      return this.isSameTheme(pattern_obj._data.cv_image_attrs._data.mcon_theme_id);
    }

    return false;
  }

  // theme이 존재하는지
  public hasTheme() {
    return this.getCurrentMconThemes().length > 0;
  }

  public getCurrentPrivateMconThemes() {
    return pipe(
      this.getCurrentMconThemes(),
      filter((mcon_theme) => mcon_theme.license_type === MCON_LISENCE_TYPE.NON_COMMERCIAL),
      toArray,
    );
  }

  // 현재 theme과 주어진 theme이 허용 가능한지
  public isMultiThemePossible({
    mcon_theme_id,
    mcon_theme,
  }: {
    mcon_theme_id?: MconThemesId;
    mcon_theme?: MconThemes;
  }) {
    const theme = mcon_theme_id ? this.getMconTheme(mcon_theme_id) : mcon_theme;
    return theme?.custom_permission === CUSTOME_PERMISSION.NORMAL;
  }

  public isMarppleDesignPossible(mcon_theme_id: MconThemesId) {
    const theme = this.getMconTheme(mcon_theme_id);
    return theme?.custom_permission === CUSTOME_PERMISSION.NORMAL;
  }

  // 현재 적용된 themes가 마플 디자인을 허용하는지 확인
  public isMarppleDesignAllowed() {
    // 마플 디자인: 텍스트, 무료디자인, 자기 이미지
    const mcon_themes = this.getCurrentMconThemes();

    if (!mcon_themes?.length) return true;
    return pipe(
      mcon_themes,
      every((mcon_theme) => this.isMarppleDesignPossible(mcon_theme.id)),
    );
  }

  // id로 특정 theme 가져옴
  public getMconTheme(mcon_theme_id: MconThemesId) {
    return this.mcon_theme_map.get(mcon_theme_id);
  }

  // 이미지가 있는 모든 theme을 가져옴 (즉, 에디터에서 사용중인 theme)
  public getCurrentMconThemes() {
    return pipe(
      this.mcon_theme_map.values(),
      filter((theme) => theme.images && theme.images.length > 0),
      toArray,
    );
  }

  // theme 추가
  public addMconTheme(mcon_theme: MconTheme) {
    if (!this.mcon_theme_map.has(mcon_theme.id)) {
      this.mcon_theme_map.set(mcon_theme.id, mcon_theme);
    }
  }

  // theme에 images 추가
  public async setMconThemeImages({ product_faces2, svg_el, svg_els, svg_url }: ThemeImageInput) {
    // - this.mcon_theme_map 의 모든 images를 지우고
    pipe(
      this.mcon_theme_map.values(),
      each((theme) => {
        theme.images = [];
      }),
    );
    // - 다시 찾아서 넣어준다
    // - images의 경우는 항상 입력된 된 디자인으로부터 역으로 입력
    if (product_faces2 || svg_el || svg_els || svg_url) {
      const strategy = createThemeImageStrategy(
        { product_faces2, svg_el, svg_els, svg_url },
        this.mcon_theme_map,
      );
      await strategy.processImages();
    }
  }

  // mcon_theme_ids를 반환
  public static getMconThemeIds(
    { product_faces2, svg_el, svg_els, svg_url }: ThemeImageInput,
    with_hidden: boolean,
  ) {
    if (product_faces2 || svg_el || svg_els || svg_url) {
      const strategy = createThemeImageStrategy({ product_faces2, svg_el, svg_els, svg_url }, new Map());
      return strategy.getMconThemeIds(with_hidden);
    }
  }

  public static hasMarppleDesigns({ product_faces2, svg_el, svg_els }: ThemeImageInput) {
    if (product_faces2 || svg_el || svg_els) {
      const strategy = createThemeImageStrategy({ product_faces2, svg_el, svg_els }, new Map());
      return strategy.hasMarppleDesigns();
    }
  }

  public static getMconImageIds({ product_faces2, svg_url }: ThemeImageInput) {
    if (product_faces2 || svg_url) {
      const strategy = createThemeImageStrategy({ product_faces2, svg_url }, new Map());
      return strategy.getMconImageIds();
    }
  }

  public static makeTotalPrice(products_mcon_theme: ProductsMconThemes[]) {
    return pipe(
      products_mcon_theme,
      reduceLazy((acc, cur) => {
        return acc + cur.price * cur.quantity;
      }, 0),
    );
  }

  public static getBasePriceForMcon({
    base_product,
    base_product_color,
  }: {
    base_product: BaseProducts;
    base_product_color: ProductConstantS.BaseProductColorsAssociate;
  }) {
    const { price: bp_price, mcon_base_price } = base_product;
    const { price } = base_product_color._.base_product_color_faces[0]?._.base_product_face || {};

    const computed_price = (price ?? 0) + (bp_price ?? 0);
    return mcon_base_price ?? computed_price;
  }

  // 데이터로부터 인스턴스 생성
  public fromData(products_mcon_themes: ProductsMconThemesAssociateForMaker[]) {
    pipe(
      products_mcon_themes,
      each((products_mcon_theme) => {
        const mcon_theme_id = products_mcon_theme.mcon_theme_id;

        if (mcon_theme_id) {
          const image_ids = pipe(
            products_mcon_theme._.products_mcon_themes_images,
            map((image) => image.image_id),
            toArray,
          );
          const mcon_theme = products_mcon_theme._.mcon_theme;
          const mcon_theme_price_type = mcon_theme._.mcon_theme_price_type;
          const mcon_score = mcon_theme._.store;

          this.mcon_theme_map.set(mcon_theme_id, {
            ...mcon_theme,
            store: mcon_score,
            mcon_theme_price_type,
            images: image_ids,
          });
        }
      }),
    );
  }

  // 인스턴스를 객체로
  public toObject(price: number): ProductsMconThemeForMaker[] {
    return pipe(
      this.mcon_theme_map.values(),
      filter((theme) => theme.images?.length > 0),
      map((theme) => {
        return {
          quantity: theme.images.length,
          price: OMPCreatorDesignS.getMconThemePrice(price, {
            ...theme,
            _: { mcon_theme_price_type: theme.mcon_theme_price_type },
          }),
          percent: theme.mcon_theme_price_type.percent,
          mcon_theme_id: theme.id,
          _: {
            mcon_theme: {
              ...theme,
              _: {
                store: theme.store,
                mcon_theme_price_type: theme.mcon_theme_price_type,
              },
            },
            products_mcon_themes_images: theme.images.map((image_id) => {
              return {
                image_id,
              };
            }),
          },
        };
      }),
      toArray,
    );
  }

  public static getTotalMconThemePrice(
    product: {
      price_info: {
        total_mcon_theme_price?: number;
        total_mcon_theme_price_en?: number;
        total_mcon_theme_price_jp?: number;
      };
    },
    _en: '' | '_en' | '_jp',
  ) {
    return product.price_info['total_mcon_theme_price' + _en];
  }
}

abstract class ThemeImageStrategy<T> {
  constructor(
    protected mcon_theme_map: Map<number, MconTheme>,
    protected input: T,
  ) {}

  abstract processImages(): Promise<void> | void;

  abstract getMconThemeIds(): Promise<number[] | undefined> | number[];

  abstract hasMarppleDesigns(): Promise<boolean> | boolean;

  getMconImageIds(): Promise<number[]> | number[] {
    return [];
  }
}

class ProductFacesStrategy extends ThemeImageStrategy<NonNullable<ThemeImageInput['product_faces2']>> {
  processImages() {
    pipe(
      this.#extractValidDesigns(false),
      groupBy(this.#extractThemeId),
      entries,
      each(([key, value]: [string, CvObj[]]) => {
        const images = pipe(
          value,
          map(
            (design) =>
              ('image_id' in design?._data && design?._data?.image_id) ||
              ('cv_image_attrs' in design?._data
                ? design?._data?.cv_image_attrs?._data?.image_id
                : undefined),
          ),
          compact,
          toArray,
        );

        const mcon_theme_id = parseInt(key, 10) as MconThemesId;
        const mcon_theme = this.mcon_theme_map.get(mcon_theme_id);
        if (mcon_theme) {
          mcon_theme.images = pipe(images, uniq, toArray);
        }
      }),
    );
  }

  getMconThemeIds(with_hidden?: boolean) {
    return pipe(
      this.#extractValidDesigns(with_hidden),
      map(this.#extractThemeId),
      filter((mcon_theme_id) => mcon_theme_id !== undefined),
      uniq,
      toArray,
    );
  }

  hasMarppleDesigns() {
    return pipe(
      this.#extractValidDesigns(false),
      some((design) => {
        return this.#extractThemeId(design) === undefined;
      }),
    );
  }

  override getMconImageIds() {
    return pipe(
      this.#extractValidDesigns(false),
      map((design) => {
        const image_id = this.#extractMconImageId(design);
        return image_id != null ? image_id : undefined;
      }),
      filter((id) => typeof id === 'number'),
      uniq,
      toArray,
    );
  }

  #getFlattenCvDesigns(cv_objs: CvObj[], with_hidden?: boolean): CvObj[] {
    return pipe(
      cv_objs,
      filter((cv_obj) => (with_hidden ? true : cv_obj.visible)),
      map((cv_obj) => {
        if ('objects' in cv_obj && cv_obj.objects?.length) {
          return this.#getFlattenCvDesigns(cv_obj.objects, with_hidden);
        } else if ('_objects' in cv_obj && cv_obj._objects?.length) {
          return this.#getFlattenCvDesigns(cv_obj._objects, with_hidden);
        } else {
          return cv_obj;
        }
      }),
      flat,
      toArray,
    );
  }

  #extractValidDesigns(with_hidden?: boolean): CvObj[] {
    return pipe(
      this.input?.value ?? [],
      flatMap((v) => this.#getFlattenCvDesigns(v.designs, with_hidden)),
      toArray,
    );
  }

  #extractThemeId(design: CvObj): number | undefined {
    // - getUsedImageIdsFromPf2 참고
    // - _data.mcon_theme_id 들어갈 예정
    // - cv_obj._data.cv_type === ‘cv_image’  → cv_obj._data.mcon_theme_id
    // - cv_obj._data.cv_type === ‘cv_pattern’  → cv_obj._data.cv_image_attrs._data.mcon_theme_id
    if (ProductMconTheme.isCvImageObj(design)) {
      return design._data.mcon_theme_id;
    } else if (ProductMconTheme.isCvPatternObj(design)) {
      const pattern_obj: CvPatternObj = design;
      return pattern_obj._data.cv_image_attrs._data.mcon_theme_id;
    }
  }

  #extractMconImageId(design: CvObj): number | undefined {
    // - getUsedImageIdsFromPf2 참고
    // - _data.mcon_theme_id 들어갈 예정
    // - cv_obj._data.cv_type === ‘cv_image’  → cv_obj._data.mcon_theme_id
    // - cv_obj._data.cv_type === ‘cv_pattern’  → cv_obj._data.cv_image_attrs._data.mcon_theme_id
    if (ProductMconTheme.isCvImageObj(design)) {
      return design._data.mcon_theme_id && design._data.image_id;
    } else if (ProductMconTheme.isCvPatternObj(design)) {
      const pattern_obj: CvPatternObj = design;
      return (
        pattern_obj._data.cv_image_attrs._data.mcon_theme_id &&
        pattern_obj._data.cv_image_attrs._data.image_id
      );
    }
  }
}

interface ParsedSvgNode {
  name: string;
  type: string;
  value: string;
  attributes: {
    [key: string]: string;
  };
  children: ParsedSvgNode[];
}

abstract class SvgBasedStrategy<T> extends ThemeImageStrategy<T> {
  protected abstract fetchSvgText(): Promise<string>;
  #marpple_design_node_names = ['image', 'text', 'path'];
  #marpple_design_node_roles = ['selector-path', 'cutting-line-display-path'];

  private async parseSvgRoot() {
    try {
      const svg_text = await this.fetchSvgText();
      return await parseSvgText(svg_text);
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  private async traverseSvgTree<T>(visitor: (node: ParsedSvgNode, acc: T) => void, initial: T) {
    const root = await this.parseSvgRoot();
    if (!root) return initial;

    const walk = (node: ParsedSvgNode, acc: T) => {
      visitor(node, acc);
      node.children.forEach((child) => walk(child, acc));
    };

    walk(root, initial);
    return initial;
  }

  async processImages() {
    const theme_image_map = await this.traverseSvgTree<Map<number, number[]>>((node, map) => {
      const theme_id_str = node.attributes['data-mcon_theme_id'];
      const image_id_str = node.attributes['data-mpse__image_id'];
      const is_hidden = node.attributes.display === 'none';

      if (!is_hidden && theme_id_str && image_id_str) {
        const theme_id = parseInt(theme_id_str, 10);
        const image_id = parseInt(image_id_str, 10);

        if (!isNaN(theme_id) && !isNaN(image_id)) {
          if (!map.has(theme_id)) {
            map.set(theme_id, []);
          }
          map.get(theme_id)?.push(image_id);
        }
      }
    }, new Map());

    pipe(
      theme_image_map,
      each(([theme_id, image_ids]) => {
        const mcon_theme = this.mcon_theme_map.get(theme_id);
        if (mcon_theme) {
          mcon_theme.images = pipe(image_ids, uniq, toArray);
        }
      }),
    );
  }

  async getMconThemeIds(with_hidden?: boolean) {
    const theme_set = await this.traverseSvgTree<Set<number>>((node, set) => {
      const theme_id_str = node.attributes['data-mcon_theme_id'];
      const is_hidden = with_hidden ? false : node.attributes.display === 'none';
      const theme_id = parseInt(theme_id_str ?? '', 10);

      if (!is_hidden && !isNaN(theme_id)) {
        set.add(theme_id);
      }
    }, new Set());

    return Array.from(theme_set);
  }

  async hasMarppleDesigns(): Promise<boolean> {
    let found = false;

    await this.traverseSvgTree((node) => {
      const has_role = node.attributes['data-mpse__common__const__role__role'];
      const is_hidden = node.attributes.display === 'none';
      const node_name = node.name;
      if (
        !is_hidden &&
        !node.attributes['data-mcon_theme_id'] &&
        this.#marpple_design_node_names.includes(node_name)
      ) {
        if (!has_role || (has_role && !this.#marpple_design_node_roles.includes(has_role))) {
          found = true;
        }
      }
    }, undefined);

    return found;
  }

  override async getMconImageIds() {
    const image_set = await this.traverseSvgTree<Set<number>>((node, set) => {
      const theme_id_str = node.attributes['data-mcon_theme_id'];
      const is_hidden = node.attributes.display === 'none';
      const image_id_str = node.attributes['data-mpse__image_id'];
      if (!is_hidden && theme_id_str && image_id_str) {
        const image_id = parseInt(image_id_str, 10);
        if (!isNaN(image_id)) {
          set.add(image_id);
        }
      }
    }, new Set());

    return Array.from(image_set);
  }
}

class SvgElementsStrategy extends SvgBasedStrategy<NonNullable<ThemeImageInput['svg_els']>> {
  protected async fetchSvgText(): Promise<string> {
    const body = this.input.map((el) => el.outerHTML).join('\n');

    return `<svg xmlns="http://www.w3.org/2000/svg">${body}</svg>`;
  }
}

class SvgElementStrategy extends SvgBasedStrategy<NonNullable<ThemeImageInput['svg_el']>> {
  protected async fetchSvgText() {
    return `<svg xmlns="http://www.w3.org/2000/svg">${this.input.outerHTML}</svg>`;
  }
}

class SvgUrlStrategy extends SvgBasedStrategy<NonNullable<ThemeImageInput['svg_url']>> {
  protected async fetchSvgText() {
    const response = await axios.get(UtilS.ensureProtocol(this.input), {
      responseType: 'text',
    });
    return response.data;
  }
}

const createThemeImageStrategy = (input: ThemeImageInput, mcon_theme_map: Map<number, MconTheme>) => {
  if (input.product_faces2) {
    return new ProductFacesStrategy(mcon_theme_map, input.product_faces2);
  } else if (input.svg_el) {
    return new SvgElementStrategy(mcon_theme_map, input.svg_el);
  } else if (input.svg_els) {
    return new SvgElementsStrategy(mcon_theme_map, input.svg_els);
  } else if (input.svg_url) {
    return new SvgUrlStrategy(mcon_theme_map, input.svg_url);
  } else {
    throw new Error('알 수 없는 input 입니다.');
  }
};
